import * as z from 'zod';

import { BankruptcyId, ReportableEventId } from '../BrandedIds';
import { zodJSInstantDate, zodJSPlainDate } from '../dates/BrandedDate';
import { AuthorizedContactPaymentPayer, BorrowerPaymentPayer, EntityPaymentPayer } from './Payments';

export const BankruptcyChapter = z.enum(['CHAPTER_7', 'CHAPTER_11', 'CHAPTER_12', 'CHAPTER_13']);
export type BankruptcyChapter = z.infer<typeof BankruptcyChapter>;

export const BankruptcyStatus = z.enum(['active', 'closed', 'archived']);
export type BankruptcyStatus = z.infer<typeof BankruptcyStatus>;

export const BankruptcyOutcome = z.enum(['dismissed', 'discharged', 'converted', 'other']);
export type BankruptcyOutcome = z.infer<typeof BankruptcyOutcome>;

export const BankruptcyFiledBy = z.discriminatedUnion('type', [
  BorrowerPaymentPayer,
  EntityPaymentPayer,
  AuthorizedContactPaymentPayer,
]);
export type BankruptcyFiledBy = z.infer<typeof BankruptcyFiledBy>;

export const Bankruptcy = z.object({
  id: BankruptcyId,
  chapter: BankruptcyChapter,
  filedAt: zodJSPlainDate,
  filedBy: BankruptcyFiledBy.array().min(1, { message: 'At least one filer required' }),
  status: BankruptcyStatus,
  statusHistory: z
    .object({
      status: BankruptcyStatus,
      changedAt: zodJSInstantDate,
    })
    .array(),
  archivedAt: zodJSInstantDate.optional(),

  /** date bankruptcy exited active status */
  endDate: zodJSPlainDate.optional(),

  /** outcome of a cancelled or archived bankruptcy */
  endOutcome: BankruptcyOutcome.optional(),

  reportableEventIds: z.array(ReportableEventId),

  createdAt: zodJSInstantDate,
  updatedAt: zodJSInstantDate,
});
export type Bankruptcy = z.infer<typeof Bankruptcy>;

export const BankruptcyInput = Bankruptcy.pick({
  id: true,
  chapter: true,
  filedAt: true,
  filedBy: true,
  endDate: true,
}).partial({ id: true });
export type BankruptcyInput = z.infer<typeof BankruptcyInput>;
